/**
 * Zod validation schemas for FileFormat entity JSONB fields
 *
 * These schemas provide runtime validation for the `metadata_json`, `schema_json`,
 * `mapping_json`, and `validation_rules_json` JSONB columns in the `file_formats` table.
 *
 * **Architecture**: Const arrays in `file-format.types.ts` are the SINGLE source of truth.
 * These Zod schemas import and reference those const arrays for validation, ensuring
 * enum values are defined exactly once with zero duplication.
 *
 * **Usage Pattern**:
 * ```typescript
 * import type { FileFormatMetadata } from './file-format.types.js';
 * import { FileFormatMetadataSchema } from './file-format.schemas.js';
 *
 * // Type from .types.ts, validation from .schemas.ts
 * const metadata: FileFormatMetadata = FileFormatMetadataSchema.parse(data);
 * ```
 *
 * Note: This is a comprehensive schema file with 78+ object schemas and 36+ enum validators.
 * Import only what you need to reduce bundle size.
 */
import { z } from 'zod';
import { ACCESS_CONTROL_LEVEL_VALUES, AML_ACTION_VALUES, APPROVAL_STATUS_VALUES, ARCHIVE_LOCATION_VALUES, BALANCE_SOURCE_VALUES, CHECKSUM_VALIDATION_VALUES, COMPOSITE_MAPPING_TYPE_VALUES, COMPRESSION_ALGORITHM_VALUES, CONDITIONAL_OPERATOR_VALUES, CONDITIONAL_RULE_TYPE_VALUES, CROSS_FIELD_VALIDATION_TYPE_VALUES, CURRENCY_MAPPING_TYPE_VALUES, DATA_CLASSIFICATION_VALUES, DELIMITER_VALUES, DUPLICATE_ACTION_VALUES, DUPLICATE_SCOPE_VALUES, 
// METADATA_JSON const arrays
ENCODING_VALUES, ENCRYPTION_ALGORITHM_VALUES, ERROR_SEVERITY_VALUES, FIELD_DATA_TYPE_VALUES, INSTITUTION_LOOKUP_SOURCE_VALUES, LINE_ENDING_VALUES, LOOKUP_TYPE_VALUES, NOTIFICATION_CHANNEL_VALUES, 
// VALIDATION_RULES_JSON const arrays
NULL_HANDLING_VALUES, PRIORITY_VALUES, // Used for ProcessingPriority, BusinessCriticality, and CPUPriority
REFERENCE_ACTION_VALUES, REFERENCE_TYPE_VALUES, REMEDIATION_ACTION_VALUES, ROUNDING_MODE_VALUES, SANCTIONS_ACTION_VALUES, 
// SCHEMA_JSON const arrays
SCHEMA_TYPE_VALUES, SCREENING_PROVIDER_VALUES, THRESHOLD_ACTION_VALUES, 
// MAPPING_JSON const arrays
TRANSFORMATION_TYPE_VALUES, VELOCITY_ACTION_VALUES, } from './file-format.types.js';
// ==================== METADATA_JSON SCHEMAS ====================
// Enum schemas derived from const arrays (single source of truth)
export const EncodingSchema = z.enum(ENCODING_VALUES);
export const DelimiterSchema = z.enum(DELIMITER_VALUES);
export const LineEndingSchema = z.enum(LINE_ENDING_VALUES);
export const ProcessingPrioritySchema = z.enum(PRIORITY_VALUES);
export const BusinessCriticalitySchema = z.enum(PRIORITY_VALUES);
export const DataClassificationSchema = z.enum(DATA_CLASSIFICATION_VALUES);
export const ArchiveLocationSchema = z.enum(ARCHIVE_LOCATION_VALUES);
export const CompressionAlgorithmSchema = z.enum(COMPRESSION_ALGORITHM_VALUES);
export const EncryptionAlgorithmSchema = z.enum(ENCRYPTION_ALGORITHM_VALUES);
export const CPUPrioritySchema = z.enum(PRIORITY_VALUES);
export const NotificationChannelSchema = z.enum(NOTIFICATION_CHANNEL_VALUES);
export const SecurityScanningSchema = z.object({
    virus_scan_enabled: z.boolean().optional(),
    content_inspection: z.boolean().optional(),
    malware_detection: z.boolean().optional(),
    scan_timeout_seconds: z.number().int().positive().optional(),
});
export const ComplianceRequirementsSchema = z.object({
    pci_dss_required: z.boolean().optional(),
    sox_compliance: z.boolean().optional(),
    gdpr_compliance: z.boolean().optional(),
    hipaa_compliance: z.boolean().optional(),
    data_classification: DataClassificationSchema.optional(),
    encryption_required: z.boolean().optional(),
    retention_period_days: z.number().int().positive().optional(),
});
export const WorkflowIntegrationSchema = z.object({
    default_workflow_config: z.string().optional(),
    auto_start_workflow: z.boolean().optional(),
    workflow_timeout_minutes: z.number().int().positive().optional(),
    parallel_processing: z.boolean().optional(),
    batch_processing: z.boolean().optional(),
    batch_size: z.number().int().positive().optional(),
    checkpoint_frequency: z.number().int().positive().optional(),
    enable_rollback: z.boolean().optional(),
});
export const NotificationTemplatesSchema = z.object({
    success_template: z.string().optional(),
    error_template: z.string().optional(),
    quarantine_template: z.string().optional(),
});
export const NotificationSettingsSchema = z.object({
    success_notification: z.boolean().optional(),
    error_notification: z.boolean().optional(),
    quarantine_notification: z.boolean().optional(),
    sla_breach_notification: z.boolean().optional(),
    recipient_emails: z.array(z.string().email()).optional(),
    escalation_emails: z.array(z.string().email()).optional(),
    notification_channels: z.array(NotificationChannelSchema).optional(),
    webhook_urls: z.array(z.string().url()).optional(),
    notification_templates: NotificationTemplatesSchema.optional(),
});
export const LifecycleTransitionDaysSchema = z.object({
    to_infrequent_access: z.number().int().nonnegative().optional(),
    to_glacier: z.number().int().nonnegative().optional(),
    to_deep_archive: z.number().int().nonnegative().optional(),
});
export const ArchiveSettingsSchema = z.object({
    archive_after_processing: z.boolean().optional(),
    archive_on_error: z.boolean().optional(),
    retention_days: z.number().int().positive().optional(),
    archive_location: ArchiveLocationSchema.optional(),
    archive_path_template: z.string().optional(),
    compression_enabled: z.boolean().optional(),
    compression_algorithm: CompressionAlgorithmSchema.optional(),
    encryption_enabled: z.boolean().optional(),
    encryption_algorithm: EncryptionAlgorithmSchema.optional(),
    lifecycle_transition_days: LifecycleTransitionDaysSchema.optional(),
});
export const ResourceLimitsSchema = z.object({
    max_memory_mb: z.number().int().positive().optional(),
    max_cpu_cores: z.number().int().positive().optional(),
    max_execution_time_seconds: z.number().int().positive().optional(),
    cpu_priority: CPUPrioritySchema.optional(),
    io_priority: z.number().int().min(0).max(10).optional(),
    network_bandwidth_limit_mbps: z.number().positive().optional(),
});
export const OutputSettingsSchema = z.object({
    file_name_template: z.string().optional(),
    s3_key_template: z.string().optional(),
    file_extension: z.string().optional(),
    mime_type: z.string().optional(),
    file_name_prefix: z.string().optional(),
});
export const AutomationTriggersSchema = z.object({
    auto_process_on_upload: z.boolean().optional(),
    trigger_on_status: z.array(z.string()).optional(),
    trigger_on_time: z.string().optional(),
    trigger_on_event: z.array(z.string()).optional(),
    conditional_trigger_rules: z.record(z.string(), z.unknown()).optional(),
});
export const SLASchema = z.object({
    processing_deadline_minutes: z.number().int().positive().optional(),
    validation_deadline_minutes: z.number().int().positive().optional(),
    notification_deadline_minutes: z.number().int().positive().optional(),
    total_turnaround_hours: z.number().positive().optional(),
    business_hours_only: z.boolean().optional(),
    excluded_days: z.array(z.string()).optional(),
});
export const FileFormatMetadataSchema = z
    .object({
    description: z.string().optional(),
    version: z.string().optional(),
    effective_date: z.string().datetime().optional(),
    deprecated_date: z.string().datetime().optional(),
    tags: z.array(z.string()).optional(),
    owner_team: z.string().optional(),
    contact_email: z.string().email().optional(),
    documentation_url: z.string().url().optional(),
    expected_frequency: z.string().optional(),
    average_file_size_mb: z.number().positive().optional(),
    peak_volume_per_day: z.number().int().nonnegative().optional(),
    encoding: EncodingSchema.optional(),
    delimiter: DelimiterSchema.optional(),
    has_header: z.boolean().optional(),
    header_row_count: z.number().int().nonnegative().optional(),
    footer_row_count: z.number().int().nonnegative().optional(),
    line_ending: LineEndingSchema.optional(),
    quote_character: z.string().length(1).optional(),
    escape_character: z.string().length(1).optional(),
    null_value_representation: z.string().optional(),
    trim_whitespace: z.boolean().optional(),
    skip_empty_lines: z.boolean().optional(),
    processing_priority: ProcessingPrioritySchema.optional(),
    business_criticality: BusinessCriticalitySchema.optional(),
    sla: SLASchema.optional(),
    security_scanning: SecurityScanningSchema.optional(),
    compliance_requirements: ComplianceRequirementsSchema.optional(),
    workflow_integration: WorkflowIntegrationSchema.optional(),
    notification_settings: NotificationSettingsSchema.optional(),
    archive_settings: ArchiveSettingsSchema.optional(),
    resource_limits: ResourceLimitsSchema.optional(),
    automation_triggers: AutomationTriggersSchema.optional(),
    generator: z.string().optional(),
    processor: z.string().optional(),
    output_settings: OutputSettingsSchema.optional(),
    custom_metadata: z.record(z.string(), z.unknown()).optional(),
})
    .passthrough();
// ==================== SCHEMA_JSON SCHEMAS ====================
export const SchemaTypeSchema = z.enum(SCHEMA_TYPE_VALUES);
export const FieldDataTypeSchema = z.enum(FIELD_DATA_TYPE_VALUES);
export const AccessControlLevelSchema = z.enum(ACCESS_CONTROL_LEVEL_VALUES);
export const FieldSecuritySchema = z.object({
    pii: z.boolean().optional(),
    pci: z.boolean().optional(),
    phi: z.boolean().optional(),
    encryption_required: z.boolean().optional(),
    masking_required: z.boolean().optional(),
    masking_pattern: z.string().optional(),
    access_control_level: AccessControlLevelSchema.optional(),
    audit_access: z.boolean().optional(),
});
export const FieldValidationRulesSchema = z.object({
    required: z.boolean().optional(),
    unique: z.boolean().optional(),
    min_length: z.number().int().nonnegative().optional(),
    max_length: z.number().int().nonnegative().optional(),
    min_value: z.number().optional(),
    max_value: z.number().optional(),
    regex_pattern: z.string().optional(),
    regex_flags: z.string().optional(),
    allowed_values: z.array(z.unknown()).optional(),
    forbidden_values: z.array(z.unknown()).optional(),
    case_sensitive: z.boolean().optional(),
    custom_validator_function: z.string().optional(),
});
export const DecimalConfigurationSchema = z.object({
    precision: z.number().int().positive().optional(),
    scale: z.number().int().nonnegative().optional(),
    thousand_separator: z.string().length(1).optional(),
    decimal_separator: z.string().length(1).optional(),
});
export const DateTimeConfigurationSchema = z.object({
    input_format: z.string().optional(),
    output_format: z.string().optional(),
    timezone: z.string().optional(),
    allow_partial_dates: z.boolean().optional(),
});
export const FieldDefinitionSchema = z.object({
    field_name: z.string(),
    field_type: FieldDataTypeSchema,
    description: z.string().optional(),
    position: z.number().int().nonnegative().optional(),
    start_position: z.number().int().positive().optional(),
    end_position: z.number().int().positive().optional(),
    width: z.number().int().positive().optional(),
    nullable: z.boolean().optional(),
    default_value: z.unknown().optional(),
    example_value: z.unknown().optional(),
    validation_rules: FieldValidationRulesSchema.optional(),
    decimal_config: DecimalConfigurationSchema.optional(),
    datetime_config: DateTimeConfigurationSchema.optional(),
    security: FieldSecuritySchema.optional(),
    dependencies: z.array(z.string()).optional(),
    conditional_requirements: z.record(z.string(), z.unknown()).optional(),
    business_rules: z.array(z.string()).optional(),
});
export const NestedSchemaDefinitionSchema = z.object({
    schema_name: z.string(),
    fields: z.array(FieldDefinitionSchema),
    description: z.string().optional(),
});
export const SchemaVersionInfoSchema = z.object({
    version: z.string(),
    effective_date: z.string().datetime().optional(),
    deprecated_date: z.string().datetime().optional(),
    changelog: z.string().optional(),
    migration_notes: z.string().optional(),
});
export const SchemaMetadataSchema = z.object({
    total_fields: z.number().int().nonnegative().optional(),
    required_fields_count: z.number().int().nonnegative().optional(),
    optional_fields_count: z.number().int().nonnegative().optional(),
    pii_fields: z.array(z.string()).optional(),
    pci_fields: z.array(z.string()).optional(),
    phi_fields: z.array(z.string()).optional(),
    encrypted_fields: z.array(z.string()).optional(),
    indexed_fields: z.array(z.string()).optional(),
    computed_fields: z.array(z.string()).optional(),
});
export const FileFormatSchemaSchema = z.object({
    schema_type: SchemaTypeSchema.optional(),
    schema_version: z.string().optional(),
    fields: z.array(FieldDefinitionSchema).optional(),
    nested_schemas: z.array(NestedSchemaDefinitionSchema).optional(),
    root_element: z.string().optional(),
    namespace: z.string().optional(),
    version_info: SchemaVersionInfoSchema.optional(),
    metadata: SchemaMetadataSchema.optional(),
    json_schema_reference: z.string().url().optional(),
    xml_schema_reference: z.string().url().optional(),
});
// ==================== MAPPING_JSON SCHEMAS ====================
export const TransformationTypeSchema = z.enum(TRANSFORMATION_TYPE_VALUES);
export const LookupTypeSchema = z.enum(LOOKUP_TYPE_VALUES);
export const LookupConfigSchema = z.object({
    lookup_type: LookupTypeSchema,
    static_mapping: z.record(z.string(), z.unknown()).optional(),
    database_table: z.string().optional(),
    database_key_column: z.string().optional(),
    database_value_column: z.string().optional(),
    api_endpoint: z.string().url().optional(),
    cache_ttl_seconds: z.number().int().nonnegative().optional(),
    fallback_value: z.unknown().optional(),
});
export const CurrencyMappingTypeSchema = z.enum(CURRENCY_MAPPING_TYPE_VALUES);
export const CurrencyMappingSchema = z.object({
    mapping_type: CurrencyMappingTypeSchema,
    currency_mappings: z.record(z.string(), z.string()).optional(),
    default_currency: z.string().optional(),
});
export const CompositeMappingTypeSchema = z.enum(COMPOSITE_MAPPING_TYPE_VALUES);
export const CompositeMappingSchema = z.object({
    composite_type: CompositeMappingTypeSchema,
    source_fields: z.array(z.string()),
    separator: z.string().optional(),
    aggregation_function: z.string().optional(),
    calculation_expression: z.string().optional(),
});
export const SourceFieldSchema = z.object({
    field_name: z.string(),
    field_position: z.number().int().nonnegative().optional(),
    field_alias: z.string().optional(),
});
export const TargetFieldSchema = z.object({
    table_name: z.string(),
    field_name: z.string(),
    field_type: z.string().optional(),
    required: z.boolean().optional(),
});
export const FieldMappingSchema = z.object({
    source_field: SourceFieldSchema,
    target_field: TargetFieldSchema,
    transformation_type: TransformationTypeSchema.optional(),
    transformation_config: z.record(z.string(), z.unknown()).optional(),
    lookup_config: LookupConfigSchema.optional(),
    currency_mapping: CurrencyMappingSchema.optional(),
    composite_mapping: CompositeMappingSchema.optional(),
    default_value: z.unknown().optional(),
    conditional_mapping: z.record(z.string(), z.unknown()).optional(),
    custom_function: z.string().optional(),
    error_handling: z.string().optional(),
});
export const RecordLevelTransformSchema = z.object({
    filter_condition: z.string().optional(),
    aggregation_rules: z.record(z.string(), z.unknown()).optional(),
    grouping_fields: z.array(z.string()).optional(),
    sorting_fields: z.array(z.string()).optional(),
    deduplication_keys: z.array(z.string()).optional(),
});
export const ApprovalStatusSchema = z.enum(APPROVAL_STATUS_VALUES);
export const MappingVersionSchema = z.object({
    version: z.string(),
    approved_by: z.string().optional(),
    approval_date: z.string().datetime().optional(),
    approval_status: ApprovalStatusSchema.optional(),
    change_log: z.string().optional(),
});
export const FileFormatMappingSchema = z.object({
    mapping_version: z.string().optional(),
    field_mappings: z.array(FieldMappingSchema).optional(),
    record_level_transforms: RecordLevelTransformSchema.optional(),
    version_info: MappingVersionSchema.optional(),
    notes: z.string().optional(),
});
// ==================== VALIDATION_RULES_JSON SCHEMAS ====================
export const NullHandlingSchema = z.enum(NULL_HANDLING_VALUES);
export const ChecksumValidationSchema = z.enum(CHECKSUM_VALIDATION_VALUES);
export const RoutingNumberValidationSchema = z.object({
    checksum_validation: ChecksumValidationSchema.optional(),
    institution_lookup: z.boolean().optional(),
    cache_lookups: z.boolean().optional(),
});
export const RoundingModeSchema = z.enum(ROUNDING_MODE_VALUES);
export const CurrencyValidationSchema = z.object({
    allowed_currencies: z.array(z.string()).optional(),
    require_iso_code: z.boolean().optional(),
    decimal_places: z.number().int().nonnegative().optional(),
    rounding_mode: RoundingModeSchema.optional(),
    min_amount: z.number().optional(),
    max_amount: z.number().optional(),
});
export const AmountFieldValidationSchema = z.object({
    amount_field_name: z.string(),
    currency_field_name: z.string().optional(),
    currency_validation: CurrencyValidationSchema.optional(),
    required: z.boolean().optional(),
    null_handling: NullHandlingSchema.optional(),
});
export const AccountValidationSchema = z.object({
    account_number_field: z.string(),
    routing_number_field: z.string().optional(),
    account_type_field: z.string().optional(),
    routing_number_validation: RoutingNumberValidationSchema.optional(),
    check_account_status: z.boolean().optional(),
    verify_ownership: z.boolean().optional(),
});
export const ErrorSeveritySchema = z.enum(ERROR_SEVERITY_VALUES);
export const RemediationActionSchema = z.enum(REMEDIATION_ACTION_VALUES);
export const FormatValidationSchema = z.object({
    expected_row_count: z.number().int().nonnegative().optional(),
    expected_column_count: z.number().int().positive().optional(),
    header_validation: z.boolean().optional(),
    expected_headers: z.array(z.string()).optional(),
    trailer_validation: z.boolean().optional(),
    control_totals: z.record(z.string(), z.unknown()).optional(),
    max_file_size_mb: z.number().positive().optional(),
    max_error_percentage: z.number().min(0).max(100).optional(),
    error_severity: ErrorSeveritySchema.optional(),
    remediation_action: RemediationActionSchema.optional(),
});
export const ConditionalOperatorSchema = z.enum(CONDITIONAL_OPERATOR_VALUES);
export const ConditionalRuleTypeSchema = z.enum(CONDITIONAL_RULE_TYPE_VALUES);
export const ConditionalValidationSchema = z.object({
    rule_name: z.string(),
    rule_type: ConditionalRuleTypeSchema,
    condition_field: z.string(),
    condition_operator: ConditionalOperatorSchema,
    condition_value: z.unknown(),
    then_rules: z.record(z.string(), z.unknown()).optional(),
    else_rules: z.record(z.string(), z.unknown()).optional(),
    error_message: z.string().optional(),
});
export const CrossFieldValidationTypeSchema = z.enum(CROSS_FIELD_VALIDATION_TYPE_VALUES);
export const CrossFieldValidationSchema = z.object({
    validation_name: z.string(),
    validation_type: CrossFieldValidationTypeSchema,
    involved_fields: z.array(z.string()),
    validation_expression: z.string().optional(),
    expected_result: z.unknown().optional(),
    tolerance: z.number().optional(),
    error_message: z.string(),
    error_severity: ErrorSeveritySchema.optional(),
});
export const DuplicateScopeSchema = z.enum(DUPLICATE_SCOPE_VALUES);
export const DuplicateActionSchema = z.enum(DUPLICATE_ACTION_VALUES);
export const DuplicateDetectionSchema = z.object({
    enabled: z.boolean(),
    key_fields: z.array(z.string()),
    scope: DuplicateScopeSchema.optional(),
    action: DuplicateActionSchema.optional(),
    case_sensitive: z.boolean().optional(),
    trim_whitespace: z.boolean().optional(),
    lookback_days: z.number().int().nonnegative().optional(),
});
export const ReferenceTypeSchema = z.enum(REFERENCE_TYPE_VALUES);
export const ReferenceActionSchema = z.enum(REFERENCE_ACTION_VALUES);
export const ReferentialIntegritySchema = z.object({
    reference_name: z.string(),
    source_field: z.string(),
    reference_type: ReferenceTypeSchema,
    reference_table: z.string().optional(),
    reference_column: z.string().optional(),
    static_values: z.array(z.unknown()).optional(),
    api_endpoint: z.string().url().optional(),
    action_on_missing: ReferenceActionSchema.optional(),
    cache_references: z.boolean().optional(),
});
export const InstitutionLookupSourceSchema = z.enum(INSTITUTION_LOOKUP_SOURCE_VALUES);
export const InstitutionValidationSchema = z.object({
    routing_number_field: z.string(),
    lookup_source: InstitutionLookupSourceSchema.optional(),
    validate_active_status: z.boolean().optional(),
    cache_lookups: z.boolean().optional(),
    error_on_inactive: z.boolean().optional(),
});
export const BalanceSourceSchema = z.enum(BALANCE_SOURCE_VALUES);
export const BalanceValidationSchema = z.object({
    balance_check_enabled: z.boolean(),
    account_field: z.string(),
    amount_field: z.string(),
    balance_source: BalanceSourceSchema.optional(),
    minimum_balance_field: z.string().optional(),
    allow_overdraft: z.boolean().optional(),
    overdraft_limit: z.number().optional(),
});
export const VelocityActionSchema = z.enum(VELOCITY_ACTION_VALUES);
export const VelocityCheckSchema = z.object({
    check_name: z.string(),
    entity_field: z.string(),
    amount_field: z.string(),
    time_window_hours: z.number().positive(),
    max_transaction_count: z.number().int().positive().optional(),
    max_total_amount: z.number().positive().optional(),
    action: VelocityActionSchema.optional(),
});
export const ThresholdActionSchema = z.enum(THRESHOLD_ACTION_VALUES);
export const ThresholdRuleSchema = z.object({
    threshold_name: z.string(),
    amount_field: z.string(),
    threshold_amount: z.number().positive(),
    action: ThresholdActionSchema,
    notification_recipients: z.array(z.string().email()).optional(),
    approval_workflow: z.string().optional(),
});
export const ScreeningProviderSchema = z.enum(SCREENING_PROVIDER_VALUES);
export const AMLActionSchema = z.enum(AML_ACTION_VALUES);
export const AMLScreeningSchema = z.object({
    enabled: z.boolean(),
    party_name_field: z.string(),
    screening_provider: ScreeningProviderSchema.optional(),
    api_endpoint: z.string().url().optional(),
    match_threshold: z.number().min(0).max(100).optional(),
    action_on_match: AMLActionSchema.optional(),
    cache_results: z.boolean().optional(),
});
export const SanctionsActionSchema = z.enum(SANCTIONS_ACTION_VALUES);
export const SanctionsScreeningSchema = z.object({
    enabled: z.boolean(),
    entity_fields: z.array(z.string()),
    screening_lists: z.array(z.string()).optional(),
    fuzzy_matching: z.boolean().optional(),
    match_threshold: z.number().min(0).max(100).optional(),
    action_on_match: SanctionsActionSchema.optional(),
});
export const FileFormatValidationRulesSchema = z
    .object({
    format_validation: FormatValidationSchema.optional(),
    amount_validation: AmountFieldValidationSchema.optional(),
    account_validation: AccountValidationSchema.optional(),
    conditional_validations: z.array(ConditionalValidationSchema).optional(),
    cross_field_validations: z.array(CrossFieldValidationSchema).optional(),
    duplicate_detection: DuplicateDetectionSchema.optional(),
    referential_integrity: z.array(ReferentialIntegritySchema).optional(),
    institution_validation: InstitutionValidationSchema.optional(),
    balance_validation: BalanceValidationSchema.optional(),
    velocity_checks: z.array(VelocityCheckSchema).optional(),
    threshold_rules: z.array(ThresholdRuleSchema).optional(),
    aml_screening: AMLScreeningSchema.optional(),
    sanctions_screening: SanctionsScreeningSchema.optional(),
    custom_validations: z.array(z.record(z.string(), z.unknown())).optional(),
})
    .passthrough();
//# sourceMappingURL=file-format.schemas.js.map